/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React, { useCallback, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { useShallow } from "zustand/react/shallow";
import InlineActionIconButton from "@/components/Button/InlineActionIconButton/InlineActionIconButton";
import { removeSlashFromPath } from "@/shared/functions/functions";
import { useRedirectStore } from "@/stores/useRedirectStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { TaskInfoOutlineIcon } from "@dotnet/telematics-design-system";

import { useAuth } from "../../../../../context/Auth/AuthProvider";
import { IntegrationsRow } from "../../../../../Pages/Integrations/types";
import { Column, ROUTES, TableRowData } from "../../../../../shared";
import useTableItemDetails from "../../../../DetailsSection/hooks/useTableItemDetails";
import useTutorialMode from "../../../../TutorialMode/hooks/useTutorialMode";
import { STEP_GROUP } from "../../../../TutorialMode/types";
import { isRowDisabled } from "../../../functions";
import { mobileTableItemStyle } from "../../../styles/styleFunctions";
import { useTableStyles } from "../../../styles/styles";
import TableCheckbox from "../../DesktopTable/components/TableCheckbox";
import InlineToggleSwitch from "../../InlineToggleSwitch/InlineToggleSwitch";
import MobileTableItemColumn from "./MobileTableItemColumn";

interface IMobileTableItemProps {
  route: ROUTES;
  row: TableRowData;
  index: number;
  isTableHeadActive: boolean;
  isFirstItem: boolean;
  isLastItem: boolean;
  hasDetails: boolean;
  rowSwitch?: any;
  tableRowSwitch?: (rowData: IntegrationsRow) => Promise<void>;
}

const MobileTableItem = ({
  route,
  row,
  index,
  isTableHeadActive,
  isFirstItem,
  isLastItem,
  hasDetails,
  rowSwitch,
  tableRowSwitch
}: IMobileTableItemProps) => {
  const { user } = useAuth();

  const isRedirectTarget = useRedirectStore(
    state => Boolean(state.takeToCompanies) && state.takeToCompanies === row.id
  );

  const setTakeToCompanies = useRedirectStore(
    state => state.setTakeToCompanies
  );

  const {
    stepGroup,
    stepIndex,
    checkIfCorrectTutorialStep,
    setTutorialDetails
  } = useTutorialMode();

  const { t } = useTranslation();

  const { getTableItemDetails } = useTableItemDetails();

  const { columns, inlineActions, selectableRows } = useTableStoreByRoute(
    route,
    useShallow(state => ({
      columns: state.columns,
      inlineActions: state.inlineActions,
      selectableRows: state.selectableRows
    }))
  );

  const currentTableName = removeSlashFromPath(route);

  const {
    activeMobileTableItemCheckbox,
    mobileTableItem,
    mobileTableInlineActionsBlock
  } = useTableStyles();

  const handleDetailsClick = useCallback(() => {
    if (checkIfCorrectTutorialStep(STEP_GROUP.TutorialNewCompanyCard, 2)) {
      setTutorialDetails({
        isTutorialOpen: false,
        stepIndex: stepIndex + 1,
        stepGroup: stepGroup,
        isTutorialActive: true
      });
    }

    getTableItemDetails({ itemId: row.id, rowData: row });
    setTakeToCompanies(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTableItemDetails, row, stepIndex, setTakeToCompanies]);

  useEffect(() => {
    if (isRedirectTarget) {
      handleDetailsClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedirectTarget, row]);

  return (
    <div css={css(mobileTableItemStyle(isFirstItem, isLastItem))}>
      {isTableHeadActive && (
        <div css={activeMobileTableItemCheckbox}>
          <TableCheckbox
            route={route}
            fromTableRow
            fromMobileTableItem
            isRowChecked={false}
            index={index}
            row={row}
            disabled={isRowDisabled(selectableRows, row)}
          />
        </div>
      )}

      <div css={mobileTableItem}>
        {columns.map(
          (column: Column) =>
            column.isActive && (
              <MobileTableItemColumn
                key={column.name}
                columnData={column}
                row={row}
                onToggleChange={tableRowSwitch}
              />
            )
        )}

        {(inlineActions.length > 0 || hasDetails) && (
          <div css={mobileTableInlineActionsBlock}>
            <div
              css={css({
                display: "flex",
                "button + button": {
                  marginLeft: "16px"
                }
              })}
            >
              {rowSwitch?.renderConditions({ user }) && (
                <InlineToggleSwitch row={row} rowSwitch={rowSwitch} />
              )}

              {inlineActions.map((action: any) => {
                if (
                  action.renderConditions === undefined ||
                  action.renderConditions({ data: row, user })
                ) {
                  return (
                    <InlineActionIconButton
                      key={action.title}
                      title={action.title}
                      icon={action.icon}
                      rowData={row}
                      clickAction={action.clickAction}
                      hasLoader={action.hasLoader}
                    />
                  );
                }
                return null;
              })}

              {hasDetails && (
                <InlineActionIconButton
                  isDetails
                  customId={`mobile_details_${index}_item`}
                  title={t(`Table##show details##${currentTableName}`)}
                  icon={<TaskInfoOutlineIcon />}
                  clickAction={handleDetailsClick}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(MobileTableItem);
